/* Third-party (non-generated) contracts of every agreement type, not just oil and gas.
   Identity is (agreementType, contractId) - contract ids are only unique within a type. */
/* One hold of a contract, with the ownership it attaches to. This is the evidence a reviewer needs
   before merging: a different county means the shared book/page is a coincidence, a different owner
   means a different lessor relationship, the same tract and owner means a straight redundancy. */
export interface IThirdPartyContractHold {
    holdId: number;
    ownershipId?: number;
    globalId?: string;
    shortGlobalId?: string;
    countyName?: string;
    stateAbbreviation?: string;
    ownerName?: string;
    decimalInterest?: number;
    grossAcres?: number;
    netAcres?: number;
}

export interface IThirdPartyLeaseListItem {
    agreementType: string;
    contractId: number;
    /* The number a landman quotes; contractId is the surrogate key and means nothing to a user. */
    agreementNumber?: string;
    countyName?: string;
    stateAbbreviation?: string;
    volume: string;
    page: string;
    instrumentNumber: string;
    contractDate?: string;
    originalLessor: string;
    originalLessee: string;
    isInHand?: boolean;
    holdCount: number;
    ownershipCount: number;
    tractCount: number;
    recordingKey?: string;
    duplicateCount: number;
    /* Populated on the duplicate-cluster view only; empty on the plain listing. */
    holds?: Array<IThirdPartyContractHold>;
}

export type MatchTier = 'High' | 'Low';

export interface IThirdPartyLeaseDuplicateCluster {
    clusterKey: string;
    matchTier: MatchTier;
    agreementType: string;
    recordingKey?: string;
    originalLessor: string;
    originalLessee: string;
    contractDate?: string;
    contractCount: number;
    holdCount: number;
    tractCount: number;
    /* Other lessors on the same recorded document - a prompt to review, not a blocker. */
    siblingLessorCount: number;
    contracts: Array<IThirdPartyLeaseListItem>;
}

export interface IThirdPartyLeasePage<T> {
    items: Array<T>;
    totalRecords: number;
    pageNumber: number;
    pageSize: number;
}

export const recordingLabel = (contract: { volume?: string; page?: string; instrumentNumber?: string }): string => {
    if (contract.instrumentNumber?.length > 0) {
        return `Inst# ${contract.instrumentNumber}`;
    }

    if (contract.volume?.length > 0) {
        /* Page is blank on the large majority of third-party leases - users put the document
           number in Volume - so do not render a dangling separator when there is no page. */
        return contract.page?.length > 0 ? `${contract.volume} / ${contract.page}` : contract.volume;
    }

    return 'No recording info';
};

/* Rebuilds a display label from a cluster's normalized recording key ("I:..." / "V:vol/page"). */
export const recordingKeyLabel = (recordingKey?: string): string => {
    if (!recordingKey) {
        return 'No recording info';
    }

    if (recordingKey.startsWith('I:')) {
        return recordingLabel({ instrumentNumber: recordingKey.substring(2) });
    }

    if (recordingKey.startsWith('V:')) {
        const [volume, page] = recordingKey.substring(2).split('/');
        return recordingLabel({ volume, page });
    }

    return recordingKey;
};



// WEBPACK FOOTER //
// ./src/models/thirdPartyLease/ThirdPartyLease.ts